import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const CalendarWidget = ({ selectedDate, onDateSelect, availableDates = [] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    
    // Don't allow past dates
    if (date < today) return false;
    
    // Check if date is in available dates (mock logic)
    const dayOfWeek = date?.getDay();
    // Available Monday to Saturday (1-6), not Sunday (0)
    return dayOfWeek !== 0;
  };

  const isDateSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date?.toDateString() === selectedDate?.toDateString();
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth?.setMonth(prev?.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateClick = (date) => {
    if (isDateAvailable(date)) {
      onDateSelect(date);
    }
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth(-1)}
          iconName="ChevronLeft"
        />
        
        <h3 className="text-lg font-heading font-semibold text-primary">
          {months?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h3>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth(1)}
          iconName="ChevronRight"
        />
      </div>
      {/* Days of Week Header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek?.map(day => (
          <div key={day} className="p-2 text-center">
            <span className="text-sm font-body font-medium text-muted-foreground">
              {day}
            </span>
          </div>
        ))}
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days?.map((date, index) => (
          <div key={index} className="aspect-square">
            {date && (
              <button
                onClick={() => handleDateClick(date)}
                disabled={!isDateAvailable(date)}
                className={`
                  w-full h-full rounded-lg text-sm font-body font-medium transition-all duration-200
                  ${isDateSelected(date)
                    ? 'bg-accent text-accent-foreground shadow-soft'
                    : isDateAvailable(date)
                    ? 'hover:bg-muted text-foreground hover:scale-105'
                    : 'text-muted-foreground cursor-not-allowed opacity-50'
                  }
                `}
              >
                {date?.getDate()}
              </button>
            )}
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-6 text-xs font-body">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-accent rounded-full"></div>
          <span className="text-muted-foreground">Selected</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-muted rounded-full"></div>
          <span className="text-muted-foreground">Available</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-muted-foreground/30 rounded-full"></div>
          <span className="text-muted-foreground">Unavailable</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;